/* commissionAccrualDisplay.js -- HOW THE COMMISSION ANSWER IS SHOWN, AND WHEN IT MUST BE ASKED (commission ship 5, 2026-10-10).
 *
 * ONE owner for the words the three screens share -- the Step-5 commission panel (the wizard, the only writer), the Payroll
 * page's read-only "Commissions:" line, and Step 9 -- and for the two roster questions the screens ask:
 *   is the answer REQUIRED?  a company with at least one COMMISSIONED rep (a sales rep with a commission rate above 0) and no
 *                            stored answer (Brian's standing rule: a blank required answer blocks, naming what is missing).
 *                            A company with no commissioned rep is not asked.
 *   is the basis asked?      at least one sales rep on a quarterly / half-year / annual cycle -- the server's own count
 *                            (is_sales_rep AND COALESCE(commission_frequency,'monthly') <> 'monthly'), through the engine's
 *                            isNonMonthlyCommissionCycle, so the panel and the route judge the same roster.
 * Every RULE of the answer itself (what is required, every refusal's words, which accounts may carry it) stays in
 * engines/commissionAccrual.js -- this file owns none of them. Plain ASCII on purpose (CLAUDE.md 12.5 rule 55): the dash the
 * screen shows is written as an escape. */
import { accruesCommissions, isNonMonthlyCommissionCycle } from '../engines/commissionAccrual';
import { accruedLeafKeysFromMeta } from '../engines/paySchedule';

export const COMMISSION_BOOKS_LABELS = { when_paid: 'Expensed when paid', accrues: 'Accrued' };
export const COMMISSION_BOOKS_EXPLANATIONS = {
  when_paid: 'Each commission is expensed in the month it is paid.',
  accrues: 'Each commission is expensed before it is paid and carried on an accrued-liability account until the payout.',
};
export const COMMISSION_ACCRUAL_BASIS_LABELS = { monthly: 'Monthly', period_end: 'At the end of each commission period' };
// N1 (c) + B1: one plain line each.
export const COMMISSION_ACCRUAL_BASIS_EXPLANATIONS = {
  monthly: "Each month's earned commission is expensed in that month, whatever the rep's commission cycle.",
  period_end: "A quarterly, half-year or annual rep's commission for the whole period is expensed in the period's last month.",
};
export const NO_ACCRUED_LINE_MESSAGE = 'No balance-sheet line is typed as an accrued liability — set one at Step 4 first.';
export const ONLY_PAYROLL_LINE_MESSAGE = 'The only accrued-liability line is the accrued-payroll account, and commissions are never carried on it — type another line as an accrued liability at Step 4 first.';

const isRep = e => !!e && (e.is_sales_rep === true || e.is_sales_rep === 'true');
// A COMMISSIONED rep: a sales rep whose commission rate is above 0.
export const isCommissionedRep = e => isRep(e) && Number(e.commission_rate) > 0;
export const commissionedRepCount = employees => (employees || []).filter(isCommissionedRep).length;
// The basis question: any sales rep on a non-monthly cycle (the route's count, the engine's predicate).
export const hasNonMonthlyCommissionReps = employees => (employees || []).some(e => isRep(e) && isNonMonthlyCommissionCycle(e.commission_frequency));

/* THE GATE. null when nothing is missing; otherwise the sentence that names it. `stored` is the settings row (snake_case, as
   the settings GET returns it). */
export function commissionAnswerMissing(employees, stored) {
  const n = commissionedRepCount(employees);
  if (!n) return null;
  if (stored && stored.commission_books) return null;
  return 'Required — choose how commissions are booked (expensed when paid, or accrued): ' + n + ' rep' + (n === 1 ? ' earns' : 's earn') + ' commission.';
}

/* The accounts the picker offers (Q5 a): the stored-'accrued' lines with a label, minus the accrued-payroll account -- read
   through the owner. When the list is empty, the plain reason. */
export function emptyAccountListMessage(meta, accruedPayrollKey) {
  return accruedLeafKeysFromMeta(meta).length ? ONLY_PAYROLL_LINE_MESSAGE : NO_ACCRUED_LINE_MESSAGE;
}

/* The stored answer in one line. `accrual` is the engine's object (commissionAccrual.js commissionAccrualFromSettings);
   `accountLabel` is the linked account's label (null = none linked). */
export function commissionAccrualSummary(accrual, accountLabel) {
  if (!accrual || !accrual.books) return 'Not answered';
  if (!accruesCommissions(accrual)) return COMMISSION_BOOKS_LABELS.when_paid;
  const on = accrual.lineKey ? ' on ' + (accountLabel || accrual.lineKey) : ', no account linked';
  const basis = accrual.basis === 'period_end' ? ', at the end of each commission period'
    : accrual.basis === 'monthly' ? ', monthly' : ', monthly';   // U13 (2026-10-30): no basis is asked when every rep is on a monthly cycle -- it reads as monthly (was ', monthly (no basis stored)')
  return COMMISSION_BOOKS_LABELS.accrues + on + basis;
}
